import { useState, useEffect } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import { useScreenType } from '../../hooks/useScreenType';

import DesktopAlertSettings from './DesktopAlertSettings';
import MobileAlertSettings from './MobileAlertSettings';
import PushNotificationSettings from '../../components/Pwa/PushNotificationSettings';

interface Stock {
  symbole: string;
  nom_societe: string;
}

interface PriceAlert {
  id: number;
  stock_symbol: string;
  alert_type: 'PRICE_ABOVE' | 'PRICE_BELOW' | 'PERCENT_CHANGE';
  threshold_value: number;
  is_active: boolean;
  is_triggered: boolean;
  triggered_at: string | null;
  created_at: string;
}

const AlertSettingsScreen: React.FC = () => {
  const { token } = useAuth();
  const { trackAction } = useGamificationContext();
  const { isMobile } = useScreenType();
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const [selectedStock, setSelectedStock] = useState('');
  const [alertType, setAlertType] = useState<'PRICE_ABOVE' | 'PRICE_BELOW' | 'PERCENT_CHANGE'>('PRICE_ABOVE');
  const [thresholdValue, setThresholdValue] = useState('');
  const [editingAlert, setEditingAlert] = useState<PriceAlert | null>(null);

  useEffect(() => {
    fetchStocks();
    fetchAlerts();
  }, [token]);

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/stocks/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        data.sort((a: Stock, b: Stock) => a.symbole.localeCompare(b.symbole));
        setStocks(data);
        if (data.length > 0 && !selectedStock) {
          setSelectedStock(data[0].symbole);
        }
      }
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStock || !thresholdValue) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/v1/alerts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stock_symbol: selectedStock,
          alert_type: alertType,
          threshold_value: parseFloat(thresholdValue),
        }),
      });

      if (response.ok) {
        setThresholdValue('');
        setError('');
        trackAction('CREATE_ALERT');
        fetchAlerts();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de la création de l\'alerte');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error creating alert:', error);
      setError('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlert = async (alertId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette alerte ?')) return;

    try {
      const response = await fetch(`${API_URL}/v1/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchAlerts();
      }
    } catch (error) {
      console.error('Error deleting alert:', error);
    }
  };

  const handleToggleActive = async (alert: PriceAlert) => {
    try {
      const response = await fetch(`${API_URL}/v1/alerts/${alert.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !alert.is_active }),
      });
      if (response.ok) {
        fetchAlerts();
      }
    } catch (error) {
      console.error('Error toggling alert:', error);
    }
  };

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'PRICE_ABOVE': return 'Prix supérieur à';
      case 'PRICE_BELOW': return 'Prix inférieur à';
      case 'PERCENT_CHANGE': return 'Variation > ';
      default: return type;
    }
  };

  const formatThreshold = (type: string, value: number) => {
    if (type === 'PERCENT_CHANGE') return `${value.toFixed(1)}%`;
    return `${value.toLocaleString('fr-FR')} FCFA`;
  };

  const childProps = {
    alerts, stocks, loading, saving, error,
    selectedStock, setSelectedStock,
    alertType, setAlertType,
    thresholdValue, setThresholdValue,
    handleCreateAlert, handleDeleteAlert, handleToggleActive,
    getAlertTypeLabel, formatThreshold,
  };

  return (
    <SiteLayout>
      <Header icon='notifications' title='Alertes' />
      <PushNotificationSettings />
      {isMobile ? (
        <MobileAlertSettings {...childProps} />
      ) : (
        <DesktopAlertSettings {...childProps} />
      )}
    </SiteLayout>
  );
};

export default AlertSettingsScreen;
